import { GlobalPropertiesSelect } from '@hortplus/properties-react';
import isMobile from 'ismobilejs';
import PropTypes from 'prop-types';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { storesContext } from '../../stores/storesContext';
import { CalculateSiteSoilMoisture } from './helpers';
import './irrigation_print.scss';
import IrrigationLegend from './IrrigationLegend';
import { withIrrigationMonitoringSites } from './IrrigationProviders';
import IrrimaxSite from './irrimax_site/IrrimaxSite';
import SiteButton from './SiteButton';
import TerraprobeSite from './TerraprobeSite';

/**
 * This is the main irrigation component, it displays all of the graphs and tables
 * for the supplied summary, readings, and strategy data.
 *
 * This is intended to be used as a child of the IrrigationMonitoringSites component.
 */
function IrrigationGraphs(props) {
  const userStore = useContext(storesContext);
  const [selectedSite, setSelectedSite] = useState(null);
  const isPrinting = props.printing;

  useEffect(() => {
    const siteId = localStorage.getItem('irrigation_monitoring_site_id');
    if (siteId !== null) {
      const site =
        userStore.propertiesStore.selectedProperty.irrigation_monitoring_sites.find(
          (s) => s.id === parseInt(siteId)
        );
      if (site) setSelectedSite(site);
    }
  }, [
    userStore.propertiesStore.selectedProperty.irrigation_monitoring_sites,
    userStore.propertiesStore.selectedPropertyId
  ]);

  const selectSite = (site) => {
    localStorage.setItem('irrigation_monitoring_site_id', site.id);
    setSelectedSite(site);
  };

  /**
   * Returns the summary for a site.
   *
   * The summary has summary has the data we need to calculate the current
   * status of the site.
   *
   * @param {object} site
   * @returns {(object|null)}
   */
  const siteSummary = (site) =>
    props.siteSummaries.find(
      (siteSummary) => siteSummary.site_id === Number(site.site_number)
    );

  /**
   * Returns the soil moisture percentage value for a site.
   *
   * The calculation finds the percentage of summary.latest_reading_date_rz1
   * within the range of the refill point to the full point.
   *
   * @param {object} site
   * @returns {(number|null)}
   */
  const siteSoilMoisture = (site) => {
    const summary = siteSummary(site);
    return CalculateSiteSoilMoisture(summary);
  };

  return (
    <Fragment>
      {isMobile(window.navigator).any ? (
        <Row className='justify-content-center'>
          <Col sm={10} md={8} lg={6}>
            <GlobalPropertiesSelect
              selectedDatabase={userStore.selectedDatabase}
              bearerToken={userStore.bearerToken}
              refreshBearerToken={userStore.refreshCallback}
              propertiesStore={userStore.propertiesStore}
            />
          </Col>
        </Row>
      ) : null}

      <IrrigationLegend className='mt-2 text-center' />

      {/* Irrigation Monitoring Sites Buttons */}
      <div className='d-flex justify-content-center flex-wrap mt-1'>
        {userStore.propertiesStore.selectedProperty.irrigation_monitoring_sites.map(
          (site) => {
            const soilMoisture = siteSoilMoisture(site);
            const summary = siteSummary(site);
            const yValue = summary?.latest_reading?.rz1;
            const fullPoint = summary?.full_point;
            const isBlueAlert =
              summary?.alert_level === 3 && yValue > fullPoint;
            // color for the buttons based on where the site is in the strategy.
            return (
              <SiteButton
                key={site.id}
                site={site}
                summary={summary}
                selectedSite={selectedSite}
                selectSite={selectSite}
                soilMoisture={soilMoisture}
                isBlueAlert={isBlueAlert}
              />
            );
          }
        )}
      </div>
      {selectedSite?.type === 'terraprobe' ? (
        <TerraprobeSite
          selectedSite={selectedSite}
          siteSummaries={props.siteSummaries}
          siteReadings={props.siteReadings}
          siteStrategies={props.siteStrategies}
          isPrinting={isPrinting}
          selectSite={selectSite}
        />
      ) : selectedSite?.type === 'irrimax' ? (
        <IrrimaxSite
          site={selectedSite}
          siteSummaries={props.siteSummaries}
          selectSite={selectSite}
        />
      ) : null}
    </Fragment>
  );
}

IrrigationGraphs.propTypes = {
  siteSummaries: PropTypes.array.isRequired,
  siteReadings: PropTypes.object.isRequired,
  siteStrategies: PropTypes.object.isRequired,
  printing: PropTypes.bool
};

// Doesn't need to be wrapped in observer because withIrrigationMonitoringSites is
export default withIrrigationMonitoringSites(IrrigationGraphs);

